"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { FaqSettings, FaqStory } from "./faqs";
import { faqsStore, useDataStore } from "./site-data";
import { useOpenWithFocus } from "./use-open-with-focus";

type FaqContextValue = {
  open: boolean;
  source: string | null;
  setOpen: (open: boolean, source?: string) => void;
  faqs: FaqStory[];
  settings: FaqSettings | null;
  setSettings: (next: FaqSettings | null) => void;
  upsertFaq: (faq: FaqStory) => void;
};

const FaqContext = createContext<FaqContextValue | null>(null);

export function FaqProvider({
  children,
  faqs,
  settings,
}: {
  children: React.ReactNode;
  faqs: FaqStory[];
  settings: FaqSettings | null;
}) {
  const [open, setOpenInner] = useOpenWithFocus(false);
  const [source, setSource] = useState<string | null>(null);
  const setOpen = useCallback(
    (next: boolean, src?: string) => {
      if (next) {
        setSource(src ?? null);
        faqsStore.load().catch(() => {});
      }
      setOpenInner(next);
    },
    [setOpenInner],
  );
  const [liveSettings, setLiveSettings] = useState<FaqSettings | null>(settings);
  const [liveFaqs, setLiveFaqs] = useState<FaqStory[]>(faqs);
  const loadedFaqs = useDataStore(faqsStore);
  const allFaqs = useMemo(() => {
    if (!loadedFaqs) return liveFaqs;
    const byUuid = new Map(loadedFaqs.map((f) => [f.uuid, f]));
    for (const f of liveFaqs) byUuid.set(f.uuid, f);
    return Array.from(byUuid.values());
  }, [loadedFaqs, liveFaqs]);
  useEffect(() => {
    setLiveSettings(settings);
  }, [settings]);
  useEffect(() => {
    setLiveFaqs(faqs);
  }, [faqs]);
  const upsertFaq = useCallback((faq: FaqStory) => {
    setLiveFaqs((prev) => {
      const i = prev.findIndex((f) => f.uuid === faq.uuid);
      if (i === -1) return [...prev, faq];
      const next = prev.slice();
      next[i] = faq;
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({
      open,
      source,
      setOpen,
      faqs: allFaqs,
      settings: liveSettings,
      setSettings: setLiveSettings,
      upsertFaq,
    }),
    [open, source, setOpen, allFaqs, liveSettings, upsertFaq],
  );
  return <FaqContext.Provider value={value}>{children}</FaqContext.Provider>;
}

export function useFaqDrawer() {
  const ctx = useContext(FaqContext);
  if (!ctx) {
    throw new Error("useFaqDrawer must be used inside FaqProvider");
  }
  return ctx;
}
